import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { dataRoomService } from '../services/dataRoomService';
import {
  DataRoom,
  DataRoomFolder,
  DataRoomDocument,
  DataRoomShareLink,
  DataRoomStats,
} from '../types/dataRoom';
import {
  FiArrowLeft,
  FiShare2,
  FiFolder,
  FiPlus,
  FiFileText,
} from 'react-icons/fi';

// Type cast icons for React 18 compatibility
const ArrowLeftIcon = FiArrowLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const ShareIcon = FiShare2 as React.FC<React.SVGProps<SVGSVGElement>>;
const FolderIcon = FiFolder as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;

import FolderSection from '../components/datarooms/FolderSection';
import DeliverySettings from '../components/datarooms/DeliverySettings';
import AnalyticsPanel from '../components/datarooms/AnalyticsPanel';
import AddFolderModal from '../components/datarooms/AddFolderModal';
import UploadFileModal from '../components/datarooms/UploadFileModal';
import ShareModal from '../components/datarooms/ShareModal';
import DeleteConfirmModal from '../components/datarooms/DeleteConfirmModal';
import DocumentViewerModal from '../components/datarooms/DocumentViewerModal';

const DataRoomDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoading: authLoading, error: authError, canAccessApp } = useAuthStatus();

  // Data state
  const [dataRoom, setDataRoom] = useState<DataRoom | null>(null);
  const [shareLinks, setShareLinks] = useState<DataRoomShareLink[]>([]);
  const [stats, setStats] = useState<DataRoomStats | null>(null);

  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [addSubfolderParentId, setAddSubfolderParentId] = useState<string | undefined>(undefined);
  const [showShareModal, setShowShareModal] = useState(false);
  const [uploadingToFolder, setUploadingToFolder] = useState<{ id: string; name: string } | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<string | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<DataRoomDocument | null>(null);

  // Entry page state
  const [entryTitle, setEntryTitle] = useState('');
  const [entryContent, setEntryContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load data room
  const loadDataRoom = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const room = await dataRoomService.getDataRoom(id);
      setDataRoom(room);
      setEntryTitle(room.entry_title || '');
      setEntryContent(room.entry_content || '');
    } catch (err: any) {
      console.error('Failed to load data room:', err);
      setError(err?.message || 'Failed to load data room');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load share links and stats
  const loadShareLinksAndStats = useCallback(async () => {
    if (!id) return;
    try {
      const [links, statsData] = await Promise.all([
        dataRoomService.getShareLinks(id),
        dataRoomService.getStats(id),
      ]);
      setShareLinks(links);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load share links or stats:', err);
    }
  }, [id]);

  useEffect(() => {
    if (authLoading || authError || !canAccessApp) return;
    loadDataRoom();
    loadShareLinksAndStats();
  }, [authLoading, authError, canAccessApp, loadDataRoom, loadShareLinksAndStats]);

  // Handlers
  const handleBack = () => {
    navigate('/data-rooms');
  };

  const handleSaveSettings = async (field: string, value: boolean) => {
    if (!id || !dataRoom) return;
    setSaving(true);
    try {
      const updated = await dataRoomService.updateDataRoom(id, { [field]: value });
      setDataRoom(updated);
    } catch (err: any) {
      setError(err?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEntryPage = async () => {
    if (!id || !dataRoom) return;
    setSaving(true);
    try {
      const updated = await dataRoomService.updateDataRoom(id, {
        entry_title: entryTitle,
        entry_content: entryContent,
      });
      setDataRoom(updated);
      setHasUnsavedChanges(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save entry page');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!id || !dataRoom) return;
    setSaving(true);
    try {
      const updated = dataRoom.status === 'active'
        ? await dataRoomService.unpublishDataRoom(id)
        : await dataRoomService.publishDataRoom(id);
      setDataRoom(updated);
    } catch (err: any) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFolder = async (name: string, parentFolderId?: string) => {
    if (!id) return;
    try {
      await dataRoomService.createFolder(id, { name, parent_folder_id: parentFolderId });
      await loadDataRoom();
    } catch (err: any) {
      console.error('Failed to create folder:', err);
      setError(err?.message || 'Failed to create folder');
      throw err; // Re-throw so modal can handle it
    }
  };

  const handleDeleteFolder = async () => {
    if (!id || !deletingFolder) return;
    try {
      await dataRoomService.deleteFolder(id, deletingFolder);
      setDeletingFolder(null);
      await loadDataRoom();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete folder');
    }
  };

  const handleUploadFile = async (files: File[]) => {
    if (!id || !uploadingToFolder) return;
    const errors: string[] = [];
    for (const file of files) {
      try {
        await dataRoomService.uploadDocument(id, uploadingToFolder.id, file);
      } catch (err: any) {
        console.error(`Failed to upload file ${file.name}:`, err);
        errors.push(`${file.name}: ${err?.message || 'Upload failed'}`);
      }
    }
    await loadDataRoom();
    await loadShareLinksAndStats();
    if (errors.length > 0) {
      const msg = errors.length === files.length
        ? 'Failed to upload files'
        : `Some files failed to upload:\n${errors.join('\n')}`;
      setError(msg);
      throw new Error(msg);
    }
  };

  const handleDeleteDocument = async () => {
    if (!id || !deletingDocument) return;
    try {
      await dataRoomService.deleteDocument(id, deletingDocument);
      setDeletingDocument(null);
      await loadDataRoom();
      await loadShareLinksAndStats();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete document');
    }
  };

  const handleCreateShareLink = async () => {
    if (!id) throw new Error('No data room ID');
    const link = await dataRoomService.createShareLink(id);
    setShareLinks((prev) => [...prev, link]);
    await loadShareLinksAndStats();
    return link;
  };

  const handleDeleteShareLink = async (linkId: string) => {
    if (!id) return;
    await dataRoomService.deleteShareLink(id, linkId);
    setShareLinks((prev) => prev.filter((l) => l.id !== linkId));
    await loadShareLinksAndStats();
  };

  const handleToggleShareLink = async (linkId: string, isActive: boolean) => {
    if (!id) return;
    const updated = await dataRoomService.updateShareLink(id, linkId, isActive);
    setShareLinks((prev) => prev.map((l) => (l.id === linkId ? updated : l)));
    await loadShareLinksAndStats();
  };

  // Get documents for a folder
  const getDocumentsForFolder = (folderId: string): DataRoomDocument[] => {
    return dataRoom?.folders?.find((f) => f.id === folderId)?.documents || [];
  };

  // Early returns
  if (authError) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4 text-red-600">Authentication Error</h1>
        <p className="text-red-500">{authError}</p>
      </div>
    );
  }

  if (!canAccessApp) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p>You don&apos;t have access to this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="su-dataroom-detail">
        <div className="su-dataroom-detail-header">
          <div className="su-dataroom-detail-title">
            <button className="su-dataroom-back-btn" onClick={handleBack}>
              <ArrowLeftIcon />
            </button>
            <div>
              <div className="h-7 w-48 bg-gray-800 rounded animate-pulse" />
              <div className="h-4 w-24 bg-gray-800 rounded animate-pulse mt-2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dataRoom) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Data Room Not Found</h1>
        <p>The requested data room could not be found.</p>
        <button className="su-btn-secondary mt-4" onClick={handleBack}>
          Back to Data Rooms
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="su-dataroom-detail">
        {/* Header */}
        <div className="su-dataroom-detail-header">
          <div className="su-dataroom-detail-title">
            <button className="su-dataroom-back-btn" onClick={handleBack}>
              <ArrowLeftIcon />
            </button>
            <div>
              <h1 className="su-dataroom-name">{dataRoom.name}</h1>
              {dataRoom.description && (
                <p className="su-dataroom-subtitle">{dataRoom.description}</p>
              )}
            </div>
            <span
              className={dataRoom.status === 'active' ? 'su-badge-active' : 'su-badge-draft'}
            >
              {dataRoom.status}
            </span>
          </div>
          <div className="su-dataroom-actions">
            <button
              className="su-btn-secondary"
              onClick={() => setShowShareModal(true)}
            >
              <ShareIcon />
              <span>Share</span>
            </button>
            <button
              className="su-btn-primary"
              onClick={handlePublish}
              disabled={saving}
            >
              {dataRoom.status === 'active' ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              color: '#f87171',
              fontSize: '14px',
              padding: '16px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            {error}
            <button
              className="su-btn-secondary"
              style={{ marginLeft: '16px' }}
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Content */}
        <div className="su-dataroom-content">
          {/* Main content */}
          <div className="su-dataroom-main">
            {/* Entry Page Section */}
            <div className="su-dataroom-section">
              <div className="su-dataroom-section-header">
                <div className="su-dataroom-section-title">
                  <FileTextIcon />
                  <span>Entry Page</span>
                </div>
                {hasUnsavedChanges && (
                  <button
                    className="su-dataroom-section-action"
                    onClick={handleSaveEntryPage}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
              <div className="su-dataroom-section-body">
                <div style={{ marginBottom: '20px' }}>
                  <label className="su-entry-label">Title</label>
                  <input
                    type="text"
                    className="su-entry-input"
                    placeholder="Welcome to our data room..."
                    value={entryTitle}
                    onChange={(e) => {
                      setEntryTitle(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
                <div>
                  <label className="su-entry-label">Content (Markdown)</label>
                  <textarea
                    className="su-entry-textarea"
                    placeholder="Write your welcome message here. Supports markdown formatting."
                    value={entryContent}
                    onChange={(e) => {
                      setEntryContent(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                  />
                  <p className="su-entry-hint">
                    Tip: Use markdown for formatting. Headers, bold, lists, and links are supported.
                  </p>
                </div>
              </div>
            </div>

            {/* Folders Section */}
            <div className="su-dataroom-section">
              <div className="su-dataroom-section-header">
                <div className="su-dataroom-section-title">
                  <FolderIcon />
                  <span>Folders & Documents</span>
                </div>
                <button
                  className="su-dataroom-section-action"
                  onClick={() => setShowAddFolderModal(true)}
                >
                  <PlusIcon width={14} height={14} />
                  Add Folder
                </button>
              </div>
              <div className="su-dataroom-section-body">
                {dataRoom.folders && dataRoom.folders.length > 0 ? (
                  <div className="su-folders-list">
                    {dataRoom.folders
                      .filter((f) => !f.parent_folder_id)
                      .sort((a, b) => a.position - b.position)
                      .map((folder) => (
                        <FolderSection
                          key={folder.id}
                          folder={folder}
                          documents={getDocumentsForFolder(folder.id)}
                          subfolders={dataRoom.folders?.filter(
                            (sf) => sf.parent_folder_id === folder.id
                          )}
                          getDocumentsForFolder={getDocumentsForFolder}
                          onUploadClick={(folderId, folderName) =>
                            setUploadingToFolder({ id: folderId, name: folderName })
                          }
                          onDeleteFolder={(folderId) => setDeletingFolder(folderId)}
                          onDeleteDocument={(docId) => setDeletingDocument(docId)}
                          onViewDocument={(doc) => setViewingDoc(doc)}
                          onAddSubfolder={(parentId) => {
                            setAddSubfolderParentId(parentId);
                            setShowAddFolderModal(true);
                          }}
                        />
                      ))}
                  </div>
                ) : (
                  <div className="su-folder-empty">
                    No folders yet. Click &quot;Add Folder&quot; to create one.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="su-dataroom-sidebar">
            <DeliverySettings
              requireEmail={dataRoom.require_email}
              requireNda={dataRoom.require_nda}
              watermarkPdfs={dataRoom.watermark_pdfs}
              allowDownloads={dataRoom.allow_downloads}
              onChange={handleSaveSettings}
              disabled={saving}
            />
            <AnalyticsPanel stats={stats} isLoading={loading} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddFolderModal && (
        <AddFolderModal
          onClose={() => {
            setShowAddFolderModal(false);
            setAddSubfolderParentId(undefined);
          }}
          onSubmit={handleAddFolder}
          folders={dataRoom.folders}
          defaultParentId={addSubfolderParentId}
        />
      )}

      {uploadingToFolder && (
        <UploadFileModal
          folderName={uploadingToFolder.name}
          onClose={() => setUploadingToFolder(null)}
          onSubmit={handleUploadFile}
        />
      )}

      {showShareModal && id && (
        <ShareModal
          dataRoomId={id ?? ''}
          dataRoomName={dataRoom.name}
          shareLinks={shareLinks}
          onClose={() => setShowShareModal(false)}
          onCreateLink={handleCreateShareLink}
          onDeleteLink={handleDeleteShareLink}
          onToggleLink={handleToggleShareLink}
        />
      )}

      {deletingFolder && (
        <DeleteConfirmModal
          title="Delete Folder"
          message="Are you sure you want to delete this folder? All documents inside will be permanently deleted. This action cannot be undone."
          onCancel={() => setDeletingFolder(null)}
          onConfirm={handleDeleteFolder}
        />
      )}

      {deletingDocument && (
        <DeleteConfirmModal
          title="Delete Document"
          message="Are you sure you want to delete this document? This action cannot be undone."
          onCancel={() => setDeletingDocument(null)}
          onConfirm={handleDeleteDocument}
        />
      )}

      {viewingDoc && (
        <DocumentViewerModal
          doc={{
            id: viewingDoc.id,
            name: viewingDoc.name,
            file_type: viewingDoc.file_type,
            file_size: viewingDoc.file_size,
            original_filename: viewingDoc.original_filename,
          }}
          fetchBytes={() => dataRoomService.fetchOwnerDocumentBytes(viewingDoc.id)}
          fetchOfficeViewerUrl={() => dataRoomService.getOwnerOfficeViewerUrl(viewingDoc.id)}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </>
  );
};

export default DataRoomDetail;
